import type { TestResults } from "../testlab/TestRunner.js";

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

function fmt(n: number, digits = 1): string {
  return n.toFixed(digits);
}

export class TestLabUI {
  private overlay = document.getElementById("testlab-overlay") as HTMLDivElement;

  show(): void {
    this.overlay.innerHTML = this.buildShell();
  }

  hide(): void {
    this.overlay.innerHTML = "";
  }

  update(phaseLabel: string, progress: number, speeds: Array<{ name: string; speed: number }>): void {
    const lbl = document.getElementById("tl-phase-label");
    const bar = document.getElementById("tl-phase-fill");
    if (lbl) lbl.textContent = phaseLabel;
    if (bar) bar.style.width = `${Math.round(progress * 100)}%`;

    for (let i = 0; i < speeds.length; i++) {
      const el = document.getElementById(`tl-speed-${i}`);
      if (el) el.textContent = `${fmt(Math.abs(speeds[i].speed), 1)} m/s`;
    }
  }

  showResults(results: TestResults): void {
    const panel = document.getElementById("tl-results-panel");
    if (!panel) return;
    panel.classList.remove("hidden");
    panel.innerHTML = this.buildResultsHTML(results);
  }

  showPartialResults(partial: Partial<TestResults>): void {
    const panel = document.getElementById("tl-results-panel");
    if (!panel) return;
    panel.classList.remove("hidden");
    panel.innerHTML = this.buildResultsHTML(partial as TestResults);
  }

  private buildShell(): string {
    return `
      <div class="tl-header">VEHICLE TEST LAB</div>
      <div id="tl-phase-label" class="tl-phase-label">READY — PRESS ENTER TO START</div>
      <div class="tl-phase-bar"><div id="tl-phase-fill" class="tl-phase-fill" style="width:0%"></div></div>
      <div class="tl-hint">ENTER start &nbsp; N next &nbsp; R reset &nbsp; P pause &nbsp; ESC exit</div>
      <div class="tl-speeds">
        <div class="tl-speeds-header">LIVE SPEEDS</div>
        <div id="tl-speeds-list"></div>
      </div>
      <div id="tl-results-panel" class="hidden"></div>
      <div class="tl-export-row hidden" id="tl-export-row">
        <button id="tl-export-btn" class="tl-export-btn">COPY RESULTS JSON</button>
        <span id="tl-copy-ok" class="tl-copy-ok hidden">COPIED!</span>
      </div>
    `;
  }

  setVehicleNames(names: string[]): void {
    const list = document.getElementById("tl-speeds-list");
    if (!list) return;
    list.innerHTML = names.map((name, i) =>
      `<div id="tl-speed-row-${i}" class="tl-speed-row"><span class="tl-vname">${esc(name)}</span><span id="tl-speed-${i}">—</span></div>`
    ).join("");
  }

  showExportButton(results: TestResults, onCopy: () => void): void {
    const row = document.getElementById("tl-export-row");
    if (row) row.classList.remove("hidden");
    const btn = document.getElementById("tl-export-btn");
    if (btn) {
      btn.onclick = () => {
        void navigator.clipboard.writeText(JSON.stringify(results, null, 2)).then(() => {
          const ok = document.getElementById("tl-copy-ok");
          if (ok) {
            ok.classList.remove("hidden");
            setTimeout(() => ok.classList.add("hidden"), 2000);
          }
        });
        onCopy();
      };
    }
  }

  private buildResultsHTML(r: TestResults): string {
    const sections: string[] = [];

    if (r.speed && r.speed.length) {
      // Best = highest top speed (most blueprint-faithful result)
      const ranked = [...r.speed].sort((a, b) => b.topSpeed - a.topSpeed);
      const bestId = ranked[0].id;
      sections.push(`
        <div class="tl-table-title">SPEED — accelerate to 95% max, then brake</div>
        <div class="tl-table">
          <div class="tl-row tl-head">
            <span>VEHICLE</span><span>TOP</span><span>ACCEL T</span><span>ACCEL D</span><span>BRAKE D</span><span>BRAKE T</span>
          </div>
          ${ranked
            .map(
              (v) => `<div class="tl-row${v.id === bestId ? " best" : ""}">
            <span>${esc(v.name)}${v.reachedTopSpeed ? "" : " *"}</span>
            <span>${fmt(v.topSpeed)} m/s</span>
            <span>${fmt(v.accelTimeToTargetS)} s</span>
            <span>${fmt(v.accelDistanceM)} m</span>
            <span>${fmt(v.brakeDistanceM)} m</span>
            <span>${fmt(v.brakeTimeS)} s</span>
          </div>`,
            )
            .join("")}
        </div>
        <div class="tl-note">* = timed out before reaching 95% of blueprint maxSpeed</div>
      `);
    }

    if (r.corner && r.corner.length) {
      const bestId = [...r.corner].sort((a, b) => b.headingChangeDeg - a.headingChangeDeg)[0].id;
      sections.push(`
        <div class="tl-table-title">CORNERING (full lock under throttle, 6 s)</div>
        <div class="tl-table">
          <div class="tl-row tl-head">
            <span>VEHICLE</span><span>HEADING Δ</span><span>RADIUS</span><span>ENTRY</span>
          </div>
          ${r.corner
            .sort((a, b) => b.headingChangeDeg - a.headingChangeDeg)
            .map(
              (v) => `<div class="tl-row${v.id === bestId ? " best" : ""}">
            <span>${esc(v.name)}</span>
            <span>${fmt(v.headingChangeDeg, 0)}°</span>
            <span>${v.pathRadiusM !== null ? fmt(v.pathRadiusM) + " m" : "—"}</span>
            <span>${fmt(v.entrySpeed)} m/s</span>
          </div>`,
            )
            .join("")}
        </div>
      `);
    }

    if (r.drift && r.drift.length) {
      const bestId = [...r.drift].sort((a, b) => b.lateralDisplacementM - a.lateralDisplacementM)[0].id;
      sections.push(`
        <div class="tl-table-title">HANDBRAKE DRIFT (5 s slide)</div>
        <div class="tl-table">
          <div class="tl-row tl-head">
            <span>VEHICLE</span><span>LATERAL</span><span>HEADING Δ</span><span>ENTRY</span>
          </div>
          ${r.drift
            .sort((a, b) => b.lateralDisplacementM - a.lateralDisplacementM)
            .map(
              (v) => `<div class="tl-row${v.id === bestId ? " best" : ""}">
            <span>${esc(v.name)}</span>
            <span>${fmt(v.lateralDisplacementM)} m</span>
            <span>${fmt(v.headingChangeDeg, 0)}°</span>
            <span>${fmt(v.entrySpeed)} m/s</span>
          </div>`,
            )
            .join("")}
        </div>
      `);
    }

    if (r.combat && r.combat.length) {
      // Best = lowest TTK (DNF last)
      const ranked = [...r.combat].sort((a, b) => {
        const at = a.timeToKillS ?? Infinity;
        const bt = b.timeToKillS ?? Infinity;
        return at - bt;
      });
      const bestId = ranked[0].id;
      sections.push(`
        <div class="tl-table-title">COMBAT (vs 100 HP dummy)</div>
        <div class="tl-table">
          <div class="tl-row tl-head">
            <span>VEHICLE</span><span>DMG</span><span>RELOAD</span><span>SHOTS</span><span>HITS</span><span>TTK</span>
          </div>
          ${ranked
            .map(
              (v) => `<div class="tl-row${v.id === bestId ? " best" : ""}">
            <span>${esc(v.name)}</span>
            <span>${v.damagePerShot}</span>
            <span>${v.reloadMs}ms</span>
            <span>${v.shotsFired}</span>
            <span>${v.shotsHit}</span>
            <span>${v.timeToKillS !== null ? fmt(v.timeToKillS) + "s" : "—"}</span>
          </div>`,
            )
            .join("")}
        </div>
      `);
    }

    return sections.join("");
  }
}
